import { useState, useMemo } from 'react'
import { useTranslation } from "react-i18next"
import styled from 'styled-components'
import { LabelText } from '../components/base-ui'
import CourseCard  from '../components/CourseCard'
import { cloneObject, isPrivateRoomByName } from '../helpers'
import { CourseDataType, CourseDataQuantityType, ToggleFeaturesType, PrivateRoomType } from '../types'

interface SelectCourseProps {
  isAdmin: boolean
  isAdminMode: boolean
  selectedCourses: CourseDataQuantityType[]
  coursesData: CourseDataType[]
  onBack: () => void
  onClick13More: () => void
  onClickNext: (courseDataQuantity: CourseDataQuantityType[]) => void
  currencySymbol: string
  bookingFeatures: ToggleFeaturesType
  listPrivateRoom: PrivateRoomType[]
}

const SelectCourse = ({ isAdmin, isAdminMode, selectedCourses, coursesData, onBack, onClick13More, onClickNext, currencySymbol, bookingFeatures, listPrivateRoom }: SelectCourseProps) => {
  const { t } = useTranslation()
  const [quantityCourses, setQuantityCourses] = useState<CourseDataQuantityType[]>(selectedCourses)
  const maximumCourseQuantity = isAdmin && isAdminMode? 20: 12

  const totalQuantity = useMemo(() => {
    let total = 0
    quantityCourses.forEach(course => {
      total += course.quantity
    })
    return total
  }, [quantityCourses])

  const _coursesData = coursesData.filter(course => course.course_type !== 'room')
  const _roomsData = coursesData.filter(course => course.course_type === 'room')

  const selectedPrivateRoom = quantityCourses.find(selectedCourse => isPrivateRoomByName(selectedCourse.name, listPrivateRoom) && selectedCourse.quantity > 0)
  const hasSelectPrivateRoom = selectedPrivateRoom? true: false

  return (
    <Wrapper id="step-select-course">
      <LabelText>{t('course')}</LabelText>
      <CourseCards>
        {
          _coursesData.map((course, i) => (
            <CourseCard
              course={course}
              quantityCourses={quantityCourses}
              currencySymbol={currencySymbol}
              maximumCourseQuantity={maximumCourseQuantity}
              bookingFeatures={bookingFeatures}
              totalQuantity={totalQuantity}
              onUpdateQuantityCourse={handleUpdateQuantityCourse}
              disabled={!canSelectCourse(course)}
            />
          ))
        }
      </CourseCards>

      {
        _roomsData.length > 0 && (
          <>
            <LabelTextPrivateRoom>{t('private_room')}</LabelTextPrivateRoom>
            <CourseCards>
              {
                _roomsData.map((course, i) => (
                  <CourseCard
                    course={course}
                    quantityCourses={quantityCourses}
                    currencySymbol={currencySymbol}
                    maximumCourseQuantity={maximumCourseQuantity}
                    bookingFeatures={bookingFeatures}
                    totalQuantity={totalQuantity}
                    onUpdateQuantityCourse={handleUpdateQuantityCourse}
                    disabled={!canSelectCourse(course)}
                  />
                ))
              }
            </CourseCards>
          </>
        )
      }

      <NoticeServiceCharge className="yz-text-sm">{t('notice_price_shown_excluds_vat')}</NoticeServiceCharge>

      <Footer>
        {
          isAdminMode ? (
            <BtnGroupBooking className="yuzu-btn -outline -lg" onClick={onBack}><span>{t('back')}</span></BtnGroupBooking>
          ): (
            <BtnGroupBooking className="yuzu-btn -outline -lg" onClick={onClick13More}><span>{t('group_booking')}</span>{bookingFeatures.client_booking_limit ? (<span>(13 & {t('more')})</span>): null}</BtnGroupBooking>   
          )
        }
        <div 
          className="yuzu-btn -lg" 
          onClick={() => {
            let totalQuantity = 0
            quantityCourses.forEach(_course => totalQuantity += _course.quantity)
            if (totalQuantity === 0) {
              alert(`${t('warning_must_choose_course')}!`)
              return false
            }
            onClickNext(quantityCourses.filter(_course => _course.quantity !== 0 && canSelectCourse(_course)))
          }}>
            <span>{t('next')}</span>
          </div>
      </Footer>
    </Wrapper>
  )

  function canSelectCourse(course: CourseDataType) {
    let canSelectCourse = true

    if (hasSelectPrivateRoom && selectedPrivateRoom) {
      canSelectCourse = selectedPrivateRoom.id === course.id
    }
    return canSelectCourse
  }

  function handleUpdateQuantityCourse(courseQuantity: CourseDataQuantityType) {
    const { id, quantity, number_of_party } = courseQuantity
    const _quantityCourses: CourseDataQuantityType[] = cloneObject(quantityCourses)
    const targetQuantityCourse = _quantityCourses.find(course => course.id === id)
    if (targetQuantityCourse) {
      targetQuantityCourse.quantity = quantity
      targetQuantityCourse.number_of_party = number_of_party
    } else {
      _quantityCourses.push(courseQuantity)
    }
    setQuantityCourses(_quantityCourses)
  }
}

export default SelectCourse

const Wrapper = styled.div`
`

const LabelTextPrivateRoom = styled(LabelText)`
  margin-top: 32px;

  @media (max-width: 767px) {
    margin-top: 16px;
    margin-bottom: 8px;
    display: block;
  }
`

const CourseCards = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-left: -12px;
  margin-right: -12px;
  margin-bottom: -24px;

  @media (max-width: 767px) {
    margin-bottom: -8px;
  }
`

const NoticeServiceCharge = styled.div` 
  margin-top: 15px;
  color: white;
  text-align: right;
`

const Footer = styled.div`
  margin-top: 64px;
  margin-bottom: 80px;
  display: flex;
  justify-content: space-between;
`

const BtnGroupBooking = styled.div`
  &.yuzu-btn {
    padding-left: 48px !important;
    padding-right: 48px !important;

    @media (max-width: 767px) {
      padding-left: 16px !important;
      padding-right: 16px !important;
      flex-direction: column;
      align-items: center;
      padding-bottom: 18px;
    }
  }

  span:first-child::after {
    content: '';
    margin-right: .25em;
  }

  @media (max-width: 767px) {
    span:nth-child(2) {
      font-size: 10px;
    }
  }
`